import { isAndroid, isIOS } from 'react-device-detect';

/**
 * Detects if the application is running in an Android WebView
 *
 * This function checks for various indicators that suggest the app is running
 * inside an Android WebView, including:
 * - Standard WebView user agent markers
 * - Chrome version patterns for older WebViews
 * - React Native WebView bridge
 *
 * @returns {boolean} true if running in Android WebView, false otherwise
 */
export const isAndroidWebView = (): boolean => {
  if (typeof window === 'undefined' || !window.navigator) return false;

  // Use isAndroid from react-device-detect for reliable Android detection
  if (!isAndroid) return false;

  const userAgent = window.navigator.userAgent.toLowerCase();

  // Check for WebView indicators
  const hasWebViewIndicator =
    userAgent.includes('wv') || // Standard WebView indicator
    (userAgent.includes('version/') && userAgent.includes('chrome')); // Older WebView pattern

  // Additional check: In WebView, window.ReactNativeWebView might be defined
  const hasWebViewBridge =
    typeof (window as any).ReactNativeWebView !== 'undefined';

  const isWebView = hasWebViewIndicator || hasWebViewBridge;

  return isWebView;
};

/**
 * Detects if the application is running in an iOS WebView
 *
 * This function checks for various indicators that suggest the app is running
 * inside an iOS WebView, including:
 * - Standard UIWebView and WKWebView user agent markers
 * - React Native WebView bridge
 * - Standalone mode (for PWAs added to home screen)
 *
 * @returns {boolean} true if running in iOS WebView, false otherwise
 */
export const isIOSWebView = (): boolean => {
  if (typeof window === 'undefined' || !window.navigator) return false;

  // Use isIOS from react-device-detect for reliable iOS detection
  if (!isIOS) return false;

  const userAgent = window.navigator.userAgent.toLowerCase();

  // Check for WebView indicators
  // UIWebView: Contains 'mobile' but lacks 'safari'
  // WKWebView: Similar pattern, may include version info
  const hasWebViewIndicator =
    (userAgent.includes('mobile') && !userAgent.includes('safari')) ||
    userAgent.includes('wkwebview') ||
    userAgent.includes('uiwebview');

  // Check if running in standalone mode (PWA added to home screen)
  const isStandalone = (window.navigator as any).standalone === true;

  // Additional check: In WebView, window.ReactNativeWebView might be defined
  const hasWebViewBridge =
    typeof (window as any).ReactNativeWebView !== 'undefined';

  // Check for iOS-specific WebView JavaScript interface
  const hasWebKitInterface =
    typeof (window as any).webkit !== 'undefined' &&
    typeof (window as any).webkit.messageHandlers !== 'undefined';

  const isWebView =
    hasWebViewIndicator ||
    isStandalone ||
    hasWebViewBridge ||
    hasWebKitInterface;

  return isWebView;
};

/**
 * Detects if the application is running in any mobile WebView (iOS or Android)
 *
 * This function combines the detection for both iOS and Android WebViews
 * to provide a single check for any mobile WebView environment.
 *
 * @returns {boolean} true if running in any mobile WebView, false otherwise
 */
export const isMobileWebView = (): boolean => {
  return isIOSWebView() || isAndroidWebView();
};

/**
 * Gets the appropriate safe area padding for mobile devices
 *
 * Priority order:
 * 1. React Native injected insets (window.ReactNativeInsets.bottom) - most accurate
 * 2. For Android WebView: Returns 36px fallback padding
 * 3. For other mobile devices: Returns the safe area inset (usually 0)
 * 4. For desktop: Returns undefined (no padding)
 *
 * When running in React Native WebView, the app should inject accurate safe area
 * insets via window.ReactNativeInsets, which provides device-accurate values.
 *
 * @param {boolean} isMobile - Whether the device is mobile
 * @returns {string | undefined} CSS padding value or undefined
 */
export const getSafeAreaPadding = (isMobile: boolean): string | undefined => {
  if (!isMobile) return undefined;

  // Check if React Native has injected safe area insets (most accurate)
  if (
    typeof window !== 'undefined' &&
    window.ReactNativeInsets?.bottom !== undefined
  ) {
    return `${window.ReactNativeInsets.bottom}px`;
  }

  // Android WebView needs extra padding for navigation bar (fallback when not in React Native)
  if (isAndroidWebView()) {
    return '36px';
  }

  // Other mobile devices only need safe area inset
  return 'env(safe-area-inset-bottom)';
};

/**
 * Checks if the app is running inside a React Native WebView
 * (Legacy function - use isMobileWebView for more robust detection)
 */
export const isReactNativeWebView = (): boolean => {
  return typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;
};

/**
 * Posts a message to React Native WebView if available
 *
 * @param message - The message object to send to React Native
 * @returns {boolean} true if message was sent successfully, false otherwise
 */
export const postMessageToWebView = (message: any): boolean => {
  if (isReactNativeWebView()) {
    try {
      (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('Failed to post message to React Native WebView:', error);
      return false;
    }
  }
  return false;
};

/**
 * Sends a file download request to React Native WebView
 *
 * @param fileUrl - The URL of the file to download
 * @param filename - The name of the file
 * @param type - The message type to send (defaults to 'DOWNLOAD_FILE')
 * @returns {boolean} true if message was sent, false otherwise
 */
export const requestNativeFileDownload = (
  fileUrl: string,
  filename: string,
  type: string = 'DOWNLOAD_FILE',
): boolean => {
  return postMessageToWebView({
    type: type,
    payload: {
      url: fileUrl,
      filename: filename,
    },
  });
};
